import React, { useState } from "react";
import { useQueryClient } from 'react-query';
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import moment from "moment";
import 'react-datepicker/dist/react-datepicker.css';

import AssignDevicePanel from "../Dashboard/AssignDevicePanel";
import AgrismartMap from "../../components/Map/AgrismartMap";
import { Row, Column } from "../../components/Layout";
import InfoCell from "../../components/Layout/InfoCell";
import Alert from "../../components/Alert/Alert";
import WeatherWidget from "../../components/WeatherWidget";
import NTMTable from "../../components/Table/NTMTable";
import AgrismartDeviceModal from "../../components/Modal/AgrismartDeviceModal";
import CloseAlertModal from "../../components/Modal/CloseAlertModal";
import NTMButton from "../../components/Buttons/NTMButton";
import { AGRISMART, ALERTS, DEVICES, getErrorMessage, post, PROJECTS, put } from "../../service/Client";
import { useGetAlerts, useGetDevices, useGetParcels } from "../../service/AgrismartService";
import { roles } from "../../constants";
import { pinIcon } from 'img/icons'
import style from "../Dashboard/style.module.scss"


export default function AgrismartDashboard() {

	const [deviceModalOpen, setDeviceModalOpen] = useState()
	const [deviceFields, setDeviceFields] = useState()
	const [closeAlertModalOpen, setCloseAlertModalOpen] = useState();
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	let project = useSelector(store => store.session.project);
	let user = useSelector(store => store.users.data);
	let { status, devices } = useGetDevices();
	let { parcels } = useGetParcels();
	let { alerts } = useGetAlerts(false);

	function showError(e) {
		enqueueSnackbar(getErrorMessage(e), {variant: "error"});
	}

	function devicePopUp(name, ts) {
		return `<div class="bubble">
					<div class="name">id</div><div>${name}</div>
					<div class="name">ultimo contatto</div><div>${moment(ts).format("DD-MM-YYYY HH:mm")}</div>
				</div>`;
	}

	function saveDevice(deviceProps) {
		setDeviceModalOpen(false)
		setDeviceFields(null)
		put(`${AGRISMART}/${DEVICES}/${deviceProps.device}`, { body: deviceProps })
			.then(() => queryClient.invalidateQueries('devices'))
			.catch(showError);
	}

	function closeAlert(silUntil) {
		post(`${AGRISMART}/${ALERTS}/${closeAlertModalOpen}/close`, {params: {silUntil}})
			.then(() => queryClient.invalidateQueries('alerts'))
			.catch(showError);
		setCloseAlertModalOpen(null);
	}

	function disassociate(device) {
		put(`${PROJECTS}/${project.id}/devices`, {body:devices.filter(d => d.device !== device.device).map(d => d.device)})
			.then(() => queryClient.invalidateQueries('devices'))
			.catch(showError);
	}

	let markers = [];

	devices.forEach(d => {
		d.lat && markers.push({
			lat: d.lat,
			lng: d.lng,
			data: devicePopUp(d.device, d.receiptDate),
			icon: pinIcon
		});
	});

	let alertBoxes = alerts
		.filter(a => moment(a.openTime).isAfter(moment().subtract(10, 'days')))
		.sort((a, b) => b.openTime - a.openTime)
		.slice(0, 5)
		.map(a => {
			let dateLabel = moment(a.openTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm")
			let title = `${dateLabel} - Rilevata ${a.typeLabel} (${a.deviceLabel || a.device})`
			return {
				id: a.id,
				title,
				onSolve: () => setCloseAlertModalOpen(a.id)
			}
		})

	let tableHeader=[
		{
			Header: "ID",
			accessor: 'device',
			Cell: props => <Column>
				<Row style={{justifyContent: "space-around"}}>
					<span style={{paddingRight: "0.5rem"}}>{props.original.device}</span>
				</Row>
				<Row>
					{roles.isAdmin(user) && <NTMButton primary onClick={() => { setDeviceModalOpen(true); setDeviceFields(props.original); }}>Modifica</NTMButton>}
					{roles.isSuperUser(user) && <NTMButton primary onClick={() => disassociate(props.original)}>Rimuovi</NTMButton>}
				</Row>
			</Column>
		},
		{
			Header: "Tipo",
			accessor: 'type',
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span style={{paddingRight: "0.5rem"}}>{props.original.typeName || "-"}</span>
			</Row>
		},
		{
			Header: "Label",
			accessor: 'label',
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span style={{paddingRight: "0.5rem"}}>{props.original.label || props.original.device}</span>
			</Row>
		},
		{
			Header: <Column><div>{"Ultima ril"}</div><div>{"Batteria"}</div></Column>,
			accessor: 'receiptDate',
			Cell: props => (
				<Column>
					<div>{moment(props.original.receiptDate).tz("Europe/Rome").format("DD/MM/YYYY HH:mm")}</div>
					<div>{`${+props.original.battery || "-"}%`}</div>
				</Column>
			)
		},
		{
			Header: "Temp est",
			accessor: 'externalTemperature',
			Cell: props => (
				<Column>
					<div>{`${+props.original.externalTemperature || "-"}°`}</div>
					<div>{`${+props.original.externalTemperature2 || "-"}°`}</div>
				</Column>
			)
		},
		{
			Header: "temp suolo",
			accessor: 'soilTemperature',
			Cell: props => (
				<Column>
					<div>{`${+props.original.soilTemperature || "-"}°`}</div>
					<div>{`${+props.original.soilTemperature2 || "-"}°`}</div>
				</Column>
			)
		},
		{
			Header: "Hum atm",
			accessor: 'externalHumidity',
			Cell: props => (
				<Column>
					<div>{`${+props.original.externalHumidity || "-"}%`}</div>
					<div>{`${+props.original.externalHumidity2 || "-"}%`}</div>
				</Column>
			)
		},
		{
			Header: "Hum suolo",
			accessor: 'soilHumidity',
			Cell: props => (
				<Column>
					<div>{`${+props.original.soilHumidity || "-"} cb`}</div>
					<div>{`${+props.original.soilHumidity2 || "-"} cb`}</div>
				</Column>
			)
		},
		{
			Header: <Column><div>{"Piovosità"}</div><div>{"bagn fogliare"}</div></Column>,
			accessor: 'pluviometer',
			Cell: props => (
				<Column>
					<div>{`${+props.original.pluviometer} mm`}</div>
					<div>{`${+props.original.leafHumidity || "-"}%`}</div>
				</Column>
			)
		},
		{
			Header: <Column><div>{"Press atm"}</div><div>{"Radiazione solare"}</div></Column>,
			accessor: 'radiation',
			Cell: props => (
				<Column>
					<div>{`${+props.original.externalPressure || "-"} hPa`}</div>
					<div>{`${+props.original.radiation || "-"} W/mq`}</div>
				</Column>
			)
		},
		{
			Header: "Vento",
			accessor: 'windSpeed',
			Cell: props => (
				<Column>
					<div>{`${+props.original.windSpeed || "-"} km/h`}</div>
					<div>{props.original.windDirection}</div>
				</Column>
			)
		},
	]

	if(status === 'loading') return <div/>

	return (
		<div className={"animated fadeIn container"}>
			<Row className={style.box}>
				<AgrismartMap parcels={parcels} markers={markers} devices={devices}/>
			</Row>

			<Row>
				{
					devices.length === 0 && <Row style={{color:"#D8232A", paddingBottom:"1rem", fontWeight:"bold"}}>Non è ancora stato associato nessun dispositivo a questo progetto</Row>
				}
				{
					devices.length === 1 && <Column style={{ flex: "1 0 auto" }}>
						<div className={style.infoGrid}>
							<InfoCell name={"ID Centralina"} value={devices[0].device} />
							<InfoCell name={"Tipo"} value={devices[0].typeName} />
							<InfoCell name={"Label"} value={devices[0].label || devices[0].device} />
							<InfoCell name={"Ultima rilevazione"} value={moment(devices[0].receiptDate).format("DD/MM/YYYY - HH:mm")}/>
							<InfoCell name={"Batteria"} value={devices[0].battery} um={"%"}/>
							<InfoCell value={""} />	{/* segnaposto vuoto per allineamento */}
							
							<InfoCell name={"Temperatura atmosferica"} value={devices[0].externalTemperature} um={"°"}/>
							<InfoCell name={"Temperatura atmosferica 2"} value={devices[0].externalTemperature2} um={"°"}/>
							<InfoCell name={"Pressione atmosferica"} value={devices[0].externalPressure} um={" hPa"}/>
							<InfoCell name={"Umidità atmosferica"} value={devices[0].externalHumidity} um={"%"}/>
							<InfoCell name={"Umidità atmosferica 2"} value={devices[0].externalHumidity2} um={"%"}/>
							<InfoCell name={"Pioggia"} value={devices[0].pluviometer} um={" mm"}/>
							<InfoCell name={"Velocità del vento"} value={devices[0].windSpeed} um={" km/h"}/>
							<InfoCell name={"Direzione del vento"} value={devices[0].windDirection} />
							<InfoCell name={"Radiazione solare"} value={devices[0].radiation} um={" W/mq"}/>

							<InfoCell name={"Temperatura suolo 1"} value={devices[0].soilTemperature} um={"°"}/>
							<InfoCell name={"Temperatura suolo 2"} value={devices[0].soilTemperature2} um={"°"}/>
							<InfoCell name={"Bagnatura fogliare"} value={devices[0].leafHumidity} um={"%"}/>
							<InfoCell name={"Umidità suolo 1 (Potenziale idrico)"} value={devices[0].soilHumidity} um={" cb"}/>
							<InfoCell name={"Umidità suolo 2 (Potenziale idrico)"} value={devices[0].soilHumidity2} um={" cb"}/>
						</div>
						<Row>
							{roles.isAdmin(user) &&	<NTMButton primary onClick={() => {setDeviceModalOpen(true); setDeviceFields(devices[0])}}>Modifica</NTMButton>}
							{roles.isSuperUser(user) && <NTMButton primary onClick={() => disassociate(devices[0])}>Rimuovi</NTMButton>}
						</Row>
					</Column>
				}
				{
					devices.length > 1 &&
					<NTMTable
						header={tableHeader}
						body={devices}
						defaultPageSize={5}
					/>
				}

				<Column style={devices.length === 1 ? { flex: "0 0 280px" } : undefined}>
					{
						alertBoxes.map((a, i) => <Alert key={i} title={a.title} onSolve={roles.isAdmin(user) && a.onSolve}/>)
					}
				</Column>
			</Row>

			{devices.length > 0 &&
			<WeatherWidget
				lat={devices[0].lat}
				lng={devices[0].lng}
				location={devices[0].device}
				type="forecast"
			/>
			}
			<AssignDevicePanel devices={devices.map(d => d.device)}/>

			<AgrismartDeviceModal
				isOpen={deviceModalOpen}
				onClose={() => setDeviceModalOpen(false)}
				onSubmit={saveDevice}
				deviceFields={deviceFields}
			/>
			<CloseAlertModal
				isOpen={!!closeAlertModalOpen}
				onClose={() => setCloseAlertModalOpen(!closeAlertModalOpen)}
				onSubmit={closeAlert}
			/>
		</div>
	);

}
